import { Users, Globe, Info } from "lucide-react";
import { useTranslation } from "@/i18n";

const Testimonials = () => {
  const { t } = useTranslation();

  const founders = [
    {
      name: t('testimonials.founders.0.name'),
      role: t('testimonials.founders.0.role'),
      company: t('testimonials.founders.0.company'),
      url: t('testimonials.founders.0.url'),
      description: t('testimonials.founders.0.description'),
      initials: t('testimonials.founders.0.initials'),
    },
    {
      name: t('testimonials.founders.1.name'),
      role: t('testimonials.founders.1.role'),
      company: t('testimonials.founders.1.company'),
      url: t('testimonials.founders.1.url'),
      description: t('testimonials.founders.1.description'),
      initials: t('testimonials.founders.1.initials'),
    },
  ];

  return (
    <section className="py-20 md:py-28 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="glow-orb glow-orb-accent w-[500px] h-[500px] top-0 right-0 opacity-15" />
      </div>

      <div className="container mx-auto px-4 relative">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <Users className="w-4 h-4 text-primary" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wider">
              {t('testimonials.badge')}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            {t('testimonials.title')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('testimonials.subtitle')}
          </p>
        </div>

        {/* Narrative */}
        <div className="max-w-3xl mx-auto mb-12">
          <p className="text-center text-foreground/80 text-lg leading-relaxed">
            {t('testimonials.narrative')}
          </p>
        </div>

        {/* Founders cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {founders.map((founder, index) => (
            <div
              key={index}
              className="p-6 rounded-2xl glass-card border border-border/30 hover-lift transition-all duration-300 flex flex-col items-center text-center"
            >
              {/* Avatar */}
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mb-4 shadow-glow">
                <span className="text-xl font-bold text-primary">
                  {founder.initials}
                </span>
              </div>

              {/* Info */}
              <h3 className="font-bold text-foreground text-lg">{founder.name}</h3>
              <p className="text-primary font-medium text-sm mt-1">{founder.role}</p>
              <p className="text-muted-foreground text-sm">{founder.company}</p>

              {/* URL */}
              <div className="flex items-center gap-1.5 mt-3 mb-3">
                <Globe className="w-3.5 h-3.5 text-primary/60" />
                <a
                  href={`https://${founder.url}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary/70 text-sm font-medium hover:text-primary transition-colors underline underline-offset-2"
                >
                  {founder.url}
                </a>
              </div>

              {/* Description */}
              <p className="text-muted-foreground text-sm leading-relaxed">
                {founder.description}
              </p>
            </div>
          ))}
        </div>

        {/* Disclaimer */}
        <div className="flex items-center justify-center gap-2 mt-8">
          <Info className="w-4 h-4 text-muted-foreground/50" />
          <p className="text-xs text-muted-foreground">
            {t('testimonials.disclaimer')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
